import React, { useState, useMemo, memo, useCallback } from 'react';
import OpenHouseCard from '../components/OpenHouseCard';
import DayFilterDropdown from '../components/DayFilterDropdown';
import DemoModal from '../components/DemoModal';
import { useDemoModal } from '../hooks/useDemoModal';
import { StyleConstants } from '../StyleConstants';
import { useDemoOpenHouses } from '../services/demoDataService';
import { generateDayFilterOptions, filterOpenHousesByDay } from '../utils/dayFilterUtils';
import '../GalleryMobile.css';

const styles = {
  container: {
    width: '100%', 
    maxWidth: '100%',
    margin: 0,
    padding: 0,
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    marginTop: 0,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  noListings: {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: 'var(--grey)',
    color: '#666',
    borderRadius: '8px',
    fontSize: '16px',
  }
};

function DemoGalleryView({ isMobile, currentView }) {
  const { openHouses, loading, error } = useDemoOpenHouses();
  const [selectedDay, setSelectedDay] = useState('all');
  const { showDemoModal, closeDemoModal } = useDemoModal(currentView);

  // Memoize the day change handler to prevent unnecessary re-renders
  const handleDayChange = useCallback((e) => {
    setSelectedDay(e.target.value);
  }, []);

  const dayFilterOptions = useMemo(() => {
    return generateDayFilterOptions(openHouses);
  }, [openHouses]);

  const filteredAndSortedOpenHouses = useMemo(() => {
    if (!openHouses || !Array.isArray(openHouses)) return [];
    
    const filtered = filterOpenHousesByDay(openHouses, selectedDay);
    
    return [...filtered].sort((a, b) => {
      const startTimeDiff = new Date(a.openHouseStartTime) - new Date(b.openHouseStartTime);
      if (startTimeDiff !== 0) {
        return startTimeDiff; // Earliest first
      }
      // If start times are equal, sort by end time (earliest first)
      return new Date(a.openHouseEndTime) - new Date(b.openHouseEndTime);
    });
  }, [openHouses, selectedDay]);

  const renderOpenHouses = () => {
    if (loading) {
      return (
        <div style={{ ...styles.noListings, color: '#333' }}>
          Loading demo open houses...
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ ...styles.noListings, color: '#d32f2f' }}>
          Error loading demo data: {error.message}
        </div>
      );
    }

    if (!filteredAndSortedOpenHouses || filteredAndSortedOpenHouses.length === 0) {
      return (
        <div style={styles.noListings}>
          {selectedDay === 'all' ? 'No open houses in demo data' : 'No open houses scheduled for this day'}
        </div>
      );
    }

    return (
      <div 
        className="gallery-view-grid" 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          width: '100%',
          gap: '32px',
          marginTop: '8px',
          marginBottom: '32px'
        }}
      >
        {filteredAndSortedOpenHouses.map((openHouse) => (
          <OpenHouseCard 
            key={openHouse.id} 
            openHouse={openHouse} 
            isMobile={isMobile} 
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Demo Modal */}
      <DemoModal 
        show={showDemoModal}
        onClose={closeDemoModal}
      />

      <div style={styles.container}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{
              ...StyleConstants.sectionTitle,
              margin: 0,
              lineHeight: '32px',
              fontSize: '24px',
              fontWeight: '700'
            }}>
              Upcoming
              <span style={StyleConstants.statusCounter}>
                ({filteredAndSortedOpenHouses.length})
              </span>
            </h2>
          </div>
          
          <DayFilterDropdown
            value={selectedDay}
            options={dayFilterOptions}
            onChange={handleDayChange}
          />
        </div>
        
        {renderOpenHouses()}
      </div>
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(DemoGalleryView);
